import React from 'react'
import { ChallengeService } from '@duck/api-definitions'
import { Badge } from '@duck/ui'
import { TranslationKey, useTar } from '@/modules'
import { RelativeDateTime } from '@/components/datetime/RelativeDateTime'

interface UserFeedbackCardProps {
  userFeedback: ChallengeService.UserFeedback
}

const overallRatingToLabelMap: Record<number, TranslationKey> = {
  1: 'challenges.overallRatingLabel1',
  2: 'challenges.overallRatingLabel2',
  3: 'challenges.overallRatingLabel3',
  4: 'challenges.overallRatingLabel4',
  5: 'challenges.overallRatingLabel5',
  6: 'challenges.overallRatingLabel6',
}

const practiceNextRatingToLabelMap: Record<number, TranslationKey> = {
  1: 'challenges.practiceNextRatingLabel1',
  2: 'challenges.practiceNextRatingLabel2',
  3: 'challenges.practiceNextRatingLabel3',
}

/** Label/value row, mirroring the layout used by ChallengeDetailCards' DetailRow. */
const FeedbackRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-0.5 py-1.5 border-b border-border last:border-b-0">
    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
    <div className="text-sm text-foreground">{children}</div>
  </div>
)

/** Renders a rating as "value/max" alongside its descriptive label, when known. */
const RatingValue: React.FC<{ value: number; max: number; labelKey?: TranslationKey }> = ({
  value,
  max,
  labelKey,
}) => {
  const { t } = useTar()
  return (
    <span>
      <span className="font-medium">{value}/{max}</span>
      {labelKey && <span className="text-muted-foreground"> · {t(labelKey)}</span>}
    </span>
  )
}

export const UserFeedbackCard: React.FC<UserFeedbackCardProps> = ({ userFeedback }) => {
  const { t } = useTar()
  const showWhyNoAgree = !userFeedback.agreeWithFeedback && !!userFeedback.whyNoAgree

  return (
    <div className="bg-card border border-border rounded-md p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-foreground">{t('challenges.detailUserFeedbackFeedback')}</h3>
        {userFeedback.submissionDate && (
          <RelativeDateTime
            isoDateString={new Date(userFeedback.submissionDate).toISOString()}
            makeLabel={(time) => `${t('common.submitted')}: ${time}`}
            className="text-xs text-muted-foreground"
          />
        )}
      </div>

      <FeedbackRow label={t('userFeedback.overallRatingLabel')}>
        <RatingValue
          value={userFeedback.overallRating}
          max={6}
          labelKey={overallRatingToLabelMap[userFeedback.overallRating]}
        />
      </FeedbackRow>

      <FeedbackRow label={t('userFeedback.practiceNextRatingLabel')}>
        <RatingValue
          value={userFeedback.practiceNextRating}
          max={3}
          labelKey={practiceNextRatingToLabelMap[userFeedback.practiceNextRating]}
        />
      </FeedbackRow>

      <FeedbackRow label={t('userFeedback.agreeWithFeedbackLabel')}>
        <Badge variant={userFeedback.agreeWithFeedback ? 'secondary' : 'destructive'}>
          {userFeedback.agreeWithFeedback ? t('common.yes') : t('common.no')}
        </Badge>
      </FeedbackRow>

      {showWhyNoAgree && (
        <FeedbackRow label={t('userFeedback.whyNoAgreeLabel')}>
          <p className="whitespace-pre-wrap">{userFeedback.whyNoAgree}</p>
        </FeedbackRow>
      )}

      {userFeedback.freeText && (
        <FeedbackRow label={t('userFeedback.freeTextLabel')}>
          <p className="whitespace-pre-wrap">{userFeedback.freeText}</p>
        </FeedbackRow>
      )}
    </div>
  )
}
